// Minimal auth helpers for the Phase-1 shell.
//
// orbital-lint hands a signed JWT to the browser once T009's session-issuance
// endpoint is wired into the frontend (T030+ will wire the login form). Until
// then we just maintain the localStorage contract so the sign-out button has
// something concrete to clear and so the SWR client in later tickets can read
// it without further plumbing.
//
// Key is namespaced (`orbitallint.token`) so it never collides with
// orbital-reg's token if a developer hosts both apps on the same origin
// during local development.

const TOKEN_KEY = 'orbitallint.token';

export function getToken(): string | null {
  try {
    return globalThis.localStorage.getItem(TOKEN_KEY);
  } catch {
    // Browsers with storage disabled (private mode / locked-down extensions)
    // throw on getItem — treat as "no token" rather than crashing the shell.
    return null;
  }
}

export function setToken(token: string): void {
  try {
    globalThis.localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Storage-disabled browsers silently lose the session; the API will 401
    // on the next request and the UI will redirect to /login (T030+).
  }
}

export function clearToken(): void {
  try {
    globalThis.localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Same rationale as setToken — sign-out is best-effort against storage.
  }
}

/**
 * Sign the user out and bounce to the login route.
 *
 * Uses `window.location.assign` rather than react-router's `navigate` so the
 * entire app tree (including any SWR caches mounted in future tickets) is
 * torn down — this avoids stale "I'm authenticated" state surviving a sign-out
 * on shared machines.
 */
export function signOut(loginPath = '/login'): void {
  clearToken();
  globalThis.location.assign(loginPath);
}

/** Subset of the JWT Claims struct emitted by `auth.JWTIssuer.Issue`. */
export interface JWTClaims {
  sub?: string;
  org_id?: string;
  email?: string;
  /** One of the UserRole strings: reader|developer|maintainer|project_admin|org_admin. */
  role?: string;
  exp?: number;
}

/**
 * Decode the JWT payload without verification — for UX-only role gating.
 * The backend always enforces role on every request; this is purely cosmetic
 * (hide nav links that would 403).
 *
 * Returns null when no token is stored or the payload can't be parsed.
 */
export function decodeJWTClaims(): JWTClaims | null {
  const token = getToken();
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    // Base64url → standard base64 → binary string → JSON.
    const b64 = parts[1].replaceAll('-', '+').replaceAll('_', '/');
    return JSON.parse(atob(b64)) as JWTClaims;
  } catch {
    return null;
  }
}
